import { createContext, useState, useContext, useEffect } from "react";
import { API } from "../lib/axios";
import {
  GoogleOAuthProvider,
  googleLogout,
  useGoogleLogin,
} from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const navigate = useNavigate();

  const login = async ({ email, password }) => {
    setLoading(true);

    try {
      const res = await API.post("/auth/login", { email, password });
      localStorage.setItem("token", res.data.access_token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      API.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${res.data.access_token}`;
      // toast.success("Logged in successfully");
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
    navigate("login");
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
  
    if (savedToken) {
      const isExpired = isTokenExpired(savedToken);
      if (isExpired) {
        logout();
        toast.info("Session expired. Please login again.");
        setInitializing(false);
        return;
      }
  
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        API.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      }
    }
  
    setInitializing(false);
  }, []);
  
  // Auto Logout on Expired Token
  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken && isTokenExpired(storedToken)) {
      logout();
      toast.info("Session expired. Please login again.");
    }

    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        logout();
        toast.info("Session expired. Please login again.");
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  function isTokenExpired(token) {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return true;
      const now = Date.now() / 1000;
      return decoded.exp < now;
    } catch (err) {
      return true;
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, logout,initializing  }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
